import { formatDollars, formatDuration, formatTokens } from "../../lib/format";
import { moodForEffortOrCost, moodForSuccess } from "../../lib/moods";
import type { SessionHeader } from "../../models";

export function ScorePanel({ header }: { header: SessionHeader }) {
  const s = header.scores;
  const raw = s.raw ?? {};
  const tokensIn = raw.tokens_in;
  const tokensOut = raw.tokens_out;
  const tokensTotal = raw.tokens_total ?? (tokensIn ?? 0) + (tokensOut ?? 0);
  const aiWaitMs = raw.ai_wait_ms;
  const dollars = raw.dollars_estimate;

  return (
    <aside className="score-panel sketch pad">
      <h3 className="section-h">Scores</h3>
      <ScoreRow
        label="Success"
        value={s.overall_success}
        mood={moodForSuccess(s.overall_success)}
      />
      <ScoreRow label="Effort" value={s.human_effort} mood={moodForEffortOrCost(s.human_effort)} />
      <ScoreRow label="AI cost" value={s.ai_cost} mood={moodForEffortOrCost(s.ai_cost)} />

      <dl className="cost-detail">
        <div>
          <dt>Wait</dt>
          <dd className="mono">{formatDuration(aiWaitMs)}</dd>
        </div>
        <div title="Input = cumulative conversation context resent on every assistant call + ~600-token system prompt allowance. Output = assistant-generated text.">
          <dt>Tokens</dt>
          <dd className="mono">
            {formatTokens(tokensTotal)}
            {tokensIn != null && tokensOut != null ? (
              <span className="cost-detail-aux">
                {" "}
                ({formatTokens(tokensIn)} in / {formatTokens(tokensOut)} out)
              </span>
            ) : null}
          </dd>
        </div>
        <div title="List pricing for the source model. Cache discounts, batch API, and retrieval-augmented context (RAG) are not modelled.">
          <dt>Cost</dt>
          <dd className="mono">{formatDollars(dollars)}</dd>
        </div>
      </dl>

      <div className="issue-tally">
        <span className="badge sev-high">{s.issue_count.high} high</span>
        <span className="badge sev-med">{s.issue_count.med} med</span>
        <span className="badge sev-low">{s.issue_count.low} low</span>
      </div>
    </aside>
  );
}

function ScoreRow({ label, value, mood }: { label: string; value: number; mood: string }) {
  return (
    <div className={`score-row mood-${mood}`}>
      <span className="score-label">{label}</span>
      <span className="score-value">{value}</span>
      <span className="score-bar">
        <span style={{ width: `${value}%` }} />
      </span>
    </div>
  );
}
